import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

import { fetchCategorizedRiskLosses as fetchCategorizedRiskLossesService } from 'services/modules/crq/riskquantservice';

import { useNotification } from 'config/Notification';

const RiskScenariosPage = () => {

  // Notification
  const { openNotification } = useNotification();
  
  // State variables
  const [annualRiskLoss, setAnnualRiskLoss] = useState([]);
  
  // Redux selectors
  const companyDataContainer = useSelector((state) => state.auth.companyData);

  useEffect(() => {
    if (companyDataContainer?.companyid !== null) 
      fetchRiskHistoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDataContainer]);
  
  // useEffect fetches the risk history data based on selected company id
  const fetchRiskHistoryData = async () => {
    try {
      const response = await fetchCategorizedRiskLossesService({ company: companyDataContainer.companyid });
      if (response.status === 200 || response.status === 201) setAnnualRiskLoss(response.data);
      else openNotification(response);
    } catch (error) {
      openNotification('Server error');
    }
  };

  // Formats primary loss and secondary loss values of risk history data
  const formatLoss = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <div className='p-3'>
      <div className='mb-3'>
        <h5 className='crq-header'>Risk history data</h5>
        <hr />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="rating-value">Risk scenario</TableCell>
              <TableCell className="rating-value">Primary loss</TableCell>
              <TableCell className="rating-value">Secondary loss</TableCell>
              <TableCell className="rating-value">Minimum frequency</TableCell>
              <TableCell className="rating-value">Maximum frequency</TableCell>
              <TableCell className="rating-value">Most likely frequency</TableCell>
              <TableCell className="rating-value">Control strength</TableCell>
              <TableCell className="rating-value">Created by</TableCell>
              <TableCell className="rating-value">Created date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {annualRiskLoss.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center"><WarningIcon /> No data available</TableCell>
              </TableRow>
            ) : (
              annualRiskLoss.map((risk) => (
                <TableRow key={risk.time}>
                  <TableCell className="table-email rating-value">{risk.label}</TableCell>
                  <TableCell align="center" className="table-text">
                    {risk.primaryloss.map((primaryLoss) => (
                      <div key={primaryLoss.label} className="card mb-2 loss-card">
                        <div className="card-header">
                          <p>{primaryLoss.label}</p>
                        </div>
                        <ul className="list-group list-group-flush">
                          <li className="list-group-item">{`Max Loss: $${formatLoss(primaryLoss.maxloss)}`}</li>
                          <li className="list-group-item">{`Min Loss: $${formatLoss(primaryLoss.minloss)}`}</li>
                        </ul>
                      </div>
                    ))}
                  </TableCell>
                  <TableCell align="center" className="table-text">
                    {risk.secondary_loss.map((secondaryLoss) => (
                      <div key={secondaryLoss.label} className="card mb-2 loss-card">
                        <div className="card-header">
                          <p>{secondaryLoss.label}</p>
                        </div>
                        <ul className="list-group list-group-flush">
                          <li className="list-group-item">{`Max Loss: $${formatLoss(secondaryLoss.maxloss)}`}</li>
                          <li className="list-group-item">{`Min Loss: $${formatLoss(secondaryLoss.minloss)}`}</li>
                        </ul>
                      </div>
                    ))}
                  </TableCell>
                  <TableCell align="center" className="table-text">
                    <div className="card p-5">
                      {risk.frequency.min_frequency}
                    </div>
                  </TableCell>
                  <TableCell align="center" className="table-text">
                    <div className="card p-5">
                      {risk.frequency.max_frequency}
                    </div>
                  </TableCell>
                  <TableCell align="center" className="table-text">
                    <div className="card p-5">
                      {risk.frequency.mostlike_frequency}
                    </div>
                  </TableCell>
                  <TableCell align="center" className="table-text">
                    <div className="card p-5">
                      {risk.controlStrength}
                    </div>
                  </TableCell>
                  <TableCell align="center" className="table-text">
                    <div className="card p-5">
                      {risk.username}
                    </div>
                  </TableCell>
                  <TableCell align="center" className="table-text">
                    <div className="card p-5">
                      {risk?.createdAt?.substring(0, 10)}
                    </div>
                  </TableCell>
                </TableRow>
              )))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default RiskScenariosPage;
